/** @file INFO for a rotation key on a bone an IK constraint overrides (the key would not show). */
import { entriesByName } from '@pose2d/format/doc/entriesByName';
import type { Doc, Id, IkConstraintDoc, Warning } from '@pose2d/format/types';
import type { CommandContext } from '../commandContext';

/** Mix keys of one constraint in one animation (absolute 0..1). */
type MixKeys = readonly { v: number }[];

/**
 * Whether the constraint bends its bones over the whole animation: with mix keys, every key is at least 1 (they set
 * the mix the animation plays, whatever the setup); without, the setup mix is above 0.
 */
const drives = (setupMix: number, keys: MixKeys): boolean => (keys.length > 0 ? keys.every((k) => k.v >= 1) : setupMix > 0);

/** The INFO for `ik` driving bone `boneId`: the bone, the target, and whether the animation keys the mix or the setup sets it. */
function drivenNote(doc: Doc, ik: IkConstraintDoc, boneId: Id, keyed: boolean): Warning {
  const bone = doc.bones[boneId]?.name ?? boneId;
  const target = doc.bones[ik.target]?.name ?? ik.target;
  const mix = keyed ? 'mix keyed to 1 in this animation' : `mix ${ik.mix}`;
  return {
    code: 'INFO',
    message: `IK constraint "${ik.name}" (${mix}) drives the rotation of bone "${bone}": this key shows only where the constraint's mix is keyed below 1; key the IK target bone "${target}" or the constraint's mix instead`,
    params: { ik: ik.name, bone, target },
    path: 'target',
  };
}

/**
 * One INFO at `target` when an IK constraint bends `boneId` throughout animation `animId` (drives): its setup mix is
 * above 0 and the animation keys no mix, or the animation keys its mix and never below 1 (whatever the setup mix). The
 * solver replaces the bone's rotation (mixed by mix), so the FK rotation key shows only where mix is keyed below 1
 * (final review C15). The first such constraint by `order` is named, ties by name as the compiler orders them
 * (R12-01: never the key order of the table). Translation and scale keys get no notice.
 */
export function warnIkDriven(doc: Doc, animId: Id, boneId: Id, ctx: CommandContext): void {
  const mixKeys = doc.animations[animId]?.ik ?? {};
  const [first] = entriesByName(doc.constraints)
    .filter(([id, c]) => c.bones.includes(boneId) && drives(c.mix, mixKeys[id]?.mix ?? []))
    .sort(([, a], [, b]) => a.order - b.order);
  if (first !== undefined) ctx.warnings.push(drivenNote(doc, first[1], boneId, (mixKeys[first[0]]?.mix ?? []).length > 0));
}
